"use client"

import { Button, FilterButton, IconButton, Input, Separator } from "@/elements"
import useOutsideClick from "@/hooks/useOutsideClick"
import { ChevronLeftIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline"
import clsx from "clsx"
import { useCallback, useEffect, useRef } from "react"
import { useFilters } from "./useFilters"

interface Props {
  id: string
  buttonText: string
  hasSearch?: boolean
  children?: React.ReactNode
  onClickOverride?: () => { hasSubscription: boolean }
  onChangeSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void
  search?: string
  selectedIds?: string[]
  filterActive: boolean
  menuPosition?: "left" | "right"
  onRemoveAll: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  footerText?: string
  withHeaderSpaceOnMobile?: boolean
}

export const FilterWrapper: React.FC<Props> = ({
  id,
  buttonText,
  onChangeSearch,
  onClickOverride,
  onRemoveAll,
  search,
  hasSearch = true,
  children,
  filterActive,
  menuPosition = "left",
  footerText = "Done",
  withHeaderSpaceOnMobile = true,
}) => {
  const { currentOpenMenu, onToggleMenu, onCloseMenu, removeAllByIds } = useFilters()
  const inputRef = useRef<HTMLInputElement>(null)
  useOutsideClick(id, () => {
    if (currentOpenMenu === id) {
      onCloseMenu()
    }
  })

  const isOpen = currentOpenMenu === id

  const onClickMenuButton = () => {
    if (onClickOverride) {
      const { hasSubscription } = onClickOverride()
      if (hasSubscription) {
        onToggleMenu(id)
      }
    } else {
      onToggleMenu(id)
    }
  }

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
    }
  }, [isOpen])

  return (
    <div className="relative flex-shrink-0" id={id}>
      <FilterButton
        label={buttonText}
        onClick={onClickMenuButton}
        active={filterActive}
        onRemoveAll={onRemoveAll}
        open={isOpen}
      />
      <div
        className={clsx(
          "duration-250 fixed bottom-auto top-[72px] z-50 h-[calc(100dvh-72px)] w-full transition-opacity ease-out sm:absolute sm:top-auto sm:z-30 sm:h-fit sm:w-[320px]",
          isOpen ? "opacity-1 pointer-events-auto" : "pointer-events-none opacity-0",
          menuPosition === "right" ? "left-auto right-0" : "left-0 right-auto",
          withHeaderSpaceOnMobile ? "top-[72px] h-[calc(100dvh-72px)]" : "top-[0px] h-[calc(100dvh)]"
        )}>
        <div className="shadow-filter-button mt-0 h-full overflow-y-auto overscroll-contain border-none bg-bg-1 px-5 py-4 sm:mx-auto sm:mt-2 sm:h-auto sm:overflow-hidden sm:rounded-xl sm:border sm:border-solid sm:border-bg-3 sm:p-0">
          {hasSearch ? (
            <>
              <div className="hidden sm:block">
                <div className="flex flex-row items-center px-3 py-3">
                  <div className="pr-2">
                    <MagnifyingGlassIcon height={18} />
                  </div>
                  <input
                    ref={inputRef}
                    placeholder="Search"
                    className="w-full appearance-none border-none border-transparent p-0 outline-none focus:border-transparent focus:outline-none focus:ring-0"
                    onChange={onChangeSearch}
                    value={search}
                  />
                  {search && (
                    <div
                      className="my-auto cursor-pointer pl-2"
                      onClick={() => {
                        // @ts-ignore
                        onChangeSearch?.({ target: { value: "" } })
                      }}>
                      <XMarkIcon height={18} />
                    </div>
                  )}
                </div>
                <Separator className="bg-bg-3" />
              </div>
              <div className="block sm:hidden">
                <div className="flex flex-row gap-x-2 pb-1">
                  <IconButton Icon={ChevronLeftIcon} onClick={onCloseMenu} />
                  <Input
                    id="findme"
                    type="search"
                    placeholder="Search"
                    onChange={onChangeSearch}
                    wrapperClassName="w-full"
                    className="w-full"
                    value={search}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="block sm:hidden">
              <div className="flex flex-row justify-end pb-1">
                <IconButton Icon={XMarkIcon} onClick={onCloseMenu} variant="white" />
              </div>
            </div>
          )}
          <div className="flex flex-col gap-1 overflow-scroll px-2 py-2 sm:max-h-[380px]">{children}</div>
          <div className="absolute bottom-0 left-0 flex h-16 w-full flex-row items-center justify-between border-t border-bg-2 bg-bg-1 px-5 sm:hidden">
            <div onClick={onRemoveAll}>
              <Button variant="white">Clear</Button>
            </div>
            <Button onClick={onCloseMenu}>{footerText}</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
